/**
 * What a company *is*, fetched once per symbol and held for the page.
 *
 * **Per symbol, not per view**, and at module scope for `symbol-index.ts`'s
 * reason: the PIP, the data panel and a detached window can all be showing the
 * same ticker, and three components each holding their own copy is three
 * fetches for one paragraph.
 *
 * A failure is not cached. `undefined` usually means qv-data is not up yet, and
 * a developer who starts it after the app should get a working panel on the next
 * open rather than after a reload.
 */

import { useEffect, useState } from "react";

import { fetchCompanyProfile, type CompanyProfile } from "../api/symbols";

const cache = new Map<string, Promise<CompanyProfile | undefined>>();

/** The profile, or `undefined` while loading and on failure. */
export function loadProfile(symbol: string): Promise<CompanyProfile | undefined> {
  const id = symbol.toUpperCase();
  // No symbol, no request. A caller with nothing to ask about — a view blanked
  // because its symbol is a ratio — would otherwise fetch `/sym/profile/`,
  // which is a different endpoint shape rather than a miss.
  if (!id) return Promise.resolve(undefined);
  const existing = cache.get(id);
  if (existing) return existing;

  const pending = fetchCompanyProfile(id).then((result) => {
    if (result === undefined) cache.delete(id);
    return result;
  });
  cache.set(id, pending);
  return pending;
}

/** Drop everything. For tests, and for a host that knows the data moved. */
export function invalidateProfiles(): void {
  cache.clear();
}

/**
 * React binding. `undefined` while loading; the record once it lands.
 *
 * The symbol is captured per effect run and checked on resolution, so switching
 * ticker mid-flight cannot paint the previous company's description under the
 * new one's heading — the one bug a panel like this reliably ships with.
 */
export function useCompanyProfile(symbol: string): CompanyProfile | undefined {
  const [data, setData] = useState<CompanyProfile | undefined>(undefined);

  useEffect(() => {
    let live = true;
    setData(undefined);
    void loadProfile(symbol).then((result) => {
      if (live) setData(result);
    });
    return () => {
      live = false;
    };
  }, [symbol]);

  return data;
}
